import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { useServiceProviders } from '@/services/serviceProviders';

/**
 * Ordena profesionales según el orden que el comercio configuró en administración.
 *
 * Ni el endpoint de disponibilidad (`service_providers/date_time`) ni los profesionales
 * de un pack traen el campo `order`, así que reusamos el listado de la sucursal —que sí
 * lo trae— como mapa de orden. Los que no estén en el mapa van al final en lugar de
 * romper el orden del resto.
 *
 * El `locationId` sale de la URL para compartir la misma query key que el resto del
 * modal (así es cache hit y no un request nuevo). En la edición de reserva la ruta no
 * tiene `locationId`, y para eso está el fallback.
 */
const useProviderOrder = (fallbackLocationId?: number | null) => {
  const { locationId } = useRouter().query as { locationId?: string };
  const { data: providersByLocation } = useServiceProviders(locationId ?? fallbackLocationId);

  return useCallback(
    <T extends { id: number }>(providers?: T[] | null): T[] => {
      if (!providers?.length || !providersByLocation?.length) {
        return providers ?? [];
      }

      const orderById = new Map(providersByLocation.map(({ id, order }) => [id, order]));
      const orderOf = (id: number) => orderById.get(id) ?? Number.MAX_SAFE_INTEGER;

      return [...providers].sort((a, b) => orderOf(a.id) - orderOf(b.id));
    },
    [providersByLocation],
  );
};

export default useProviderOrder;
